import { Menu, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { ActiveP, ActiveW, AnnouncementIconA, AnnouncementIconUN, ExloreIconA, ExloreIconUn, MobileLogo, OverviewIconA, OverViewIconUn, SettingsA, SettingsUN, UnactiveD, UnactiveP, UnactiveR } from "../../../assets/icon";
import BackgroundWrapper from "../../wrapper";
import { MainHeader } from "../navbar";
import { TopBanner } from "../top-banner";
import { formatCurrency } from "../../../utils/function";
import useLogout from "../../hooks/use-logout";

const AdminDashboardLayout: React.FC = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const storedUserData = localStorage.getItem("userDetails");
    const Userdata = storedUserData ? JSON.parse(storedUserData) : null;
    const navigate = useNavigate();
    const balance = localStorage.getItem("balance");
    const closeSidebar = () => setIsSidebarOpen(false);
    const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);


    const menuItemsAdvertiser = [
        {
            path: '/admin/overview',
            activeIcon: OverviewIconA,
            inactiveIcon: OverViewIconUn,
            label: 'Overview',
        },
        {
            path: '/admin/recommender',
            activeIcon: ActiveP,
            inactiveIcon: UnactiveR,
            label: 'Recommenders',
        },
        {
            path: '/admin/advertisers',
            activeIcon: AnnouncementIconUN,
            inactiveIcon: AnnouncementIconA,
            label: 'Advertisers',
        },
        {
            path: '/admin/discovery/links',
            activeIcon: ExloreIconA,
            inactiveIcon: ExloreIconUn,
            label: ' Discovery Links',
        },
        {
            path: '/admin/payouts',
            activeIcon: ActiveW,
            inactiveIcon: UnactiveP,
            label: 'Payouts',
        },

        {
            path: '/admin/settings',
            activeIcon: SettingsA,
            inactiveIcon: SettingsUN,
            label: 'Global Settings',
        },
    ];


    const logout = useLogout();
    const handleSignOut = (e: React.MouseEvent) => {
        e.stopPropagation();
        logout();
        navigate("/login");
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    return (
        <BackgroundWrapper>
            <div className="hidden lg:block w-full">
                <MainHeader />
            </div>
            <div className="min-h-screen flex flex-col mx-auto w-full">

                <div className="flex-1 lg:px-[50px]  flex ">
                    {/* Mobile Overlay */}
                    {isSidebarOpen && (
                        <div
                            className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
                            onClick={closeSidebar}
                        />
                    )}

                    {/* Sidebar */}
                    <aside
                        className={`
                            fixed lg:sticky
                            lg:top-[166px]
                            inset-y-0 left-0 lg:mt-[150px]
                            w-[296px] bg-white border-r border-gray-200
                            lg:rounded-[16px] lg:h-screen
                            transform transition-transform duration-300 ease-in-out
                            ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
                            z-50 lg:z-0
                        `}
                    >
                        <div className="h-full flex flex-col">
                            {/* Sidebar Header - Mobile only */}
                            <div className="flex items-center justify-between p-4 lg:hidden border-b border-gray-200">
                                <MobileLogo />
                                <button
                                    onClick={closeSidebar}
                                    className="text-gray-500 hover:text-gray-700"
                                >
                                    <X size={24} />
                                </button>
                            </div>

                            {/* Navigation */}
                            <nav className="flex-1 px-4 space-y-1 py-4 lg:py-6">
                                {menuItemsAdvertiser.map((item) => {
                                    const isActive = location.pathname === item.path;
                                    const Icon = isActive ? item.activeIcon : item.inactiveIcon;

                                    return (
                                        <Link
                                            key={item.path}
                                            to={item.path}
                                            onClick={closeSidebar}
                                            className={`
          flex items-center gap-3 px-4 py-3 rounded-lg
          transition-colors duration-200
          ${isActive
                                                    ? 'bg-[#EEF2F6] text-black font-semibold'
                                                    : 'text-black font-medium hover:bg-gray-100'
                                                }
        `}
                                        >
                                            <Icon />
                                            <span className="text-[16px]">{item.label}</span>
                                        </Link>
                                    );
                                })}
                            </nav>

                        </div>
                    </aside>

                    {/* Main Content Area */}
                    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                        {/* Mobile banner — MainHeader (with its own banner) is hidden below lg, so this
                            covers the gap; matches the "hidden lg:block" MainHeader wrapper above. */}
                        <div className="lg:hidden">
                            <TopBanner />
                        </div>
                        {/* Mobile Header */}
                        <header className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex justify-between items-center">
                            <button
                                onClick={toggleSidebar}
                                className="text-gray-600 hover:text-gray-800"
                            >
                                {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
                            </button>

                            <div className="relative" ref={dropdownRef}>
                                <div
                                    className="flex items-center gap-3 cursor-pointer md:bg-white border-2 border-[#DBDEF0] bg-transparent md:border-none rounded-full py-3 px-5 shadow-sm"
                                    onClick={() => setIsOpen((prev) => !prev)}
                                >
                                    <UnactiveD />
                                    <div className="text-gray-700 lg:text-[14px] text-[12px] capitalize flex items-center gap-1 font-medium">
                                        {Userdata?.first_name}
                                        <p className="p-1 bg-gradient-to-r from-[#00A86B] to-[#6661FF] rounded-full text-white">
                                            {formatCurrency(Number(balance) || 0, 'NGN', 'symbol')}
                                        </p>
                                        <svg
                                            className={`mt-1 transform transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                                            width="14"
                                            height="14"
                                            viewBox="0 0 14 14"
                                            fill="none"
                                            xmlns="http://www.w3.org/2000/svg"
                                        >
                                            <path
                                                d="M10.8504 5.97689L7.00039 10.123L3.15039 5.97689"
                                                stroke="#121212"
                                                strokeWidth="1.05"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                            />
                                        </svg>
                                    </div>
                                </div>

                                {/* Dropdown Menu */}
                                {isOpen && (
                                    <div className="absolute right-0 mt-2 w-44 bg-white rounded-xl lg:text-[14px] text-[#121212] text-[12px] shadow-lg border border-gray-100 overflow-hidden z-50">
                                        <button onClick={() => navigate('/')} className="w-full text-left px-4 py-2 hover:bg-gray-100">
                                            Explore Rewards
                                        </button>

                                        <button onClick={handleSignOut} className="w-full text-left px-4 py-3 hover:bg-gray-100">
                                            Sign Out
                                        </button>
                                    </div>
                                )}
                            </div>
                        </header>

                        {/* Content Area */}
                        <main className="flex-1 p-4 lg:p-0 lg:ml-6">
                            <div className="w-full h-full mt-20 lg:mt-[150px]">
                                <Outlet />
                            </div>
                        </main>
                    </div>
                </div>
            </div>
        </BackgroundWrapper>
    );
};

export default AdminDashboardLayout;